import { FC, useEffect, useRef } from 'react';

import { FormikProps } from 'formik';
import { Plus, Trash2 } from 'lucide-react';

import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

import { CATEGORY_SUB_TYPES } from '../constants';

import { BroadcastFormValues } from './form';

import css from './BroadcastForm.module.css';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { Switch } from 'v2/components/switch/Switch';

interface BroadcastFormProps {
  formik: FormikProps<BroadcastFormValues>;
  onIconSelect?: (fileId: string) => void;
}

export const BroadcastForm: FC<BroadcastFormProps> = ({ formik, onIconSelect }) => {
  const prevCategorySubType = useRef(formik.values.categorySubType);

  useEffect(() => {
    if (formik.values.categorySubType !== prevCategorySubType.current) {
      prevCategorySubType.current = formik.values.categorySubType;
      formik.setFieldValue('metadata', []);
    }
  }, [formik.values.categorySubType]);

  const handleIconSelect = (fileId: string) => {
    formik.setFieldValue('iconId', fileId);
    onIconSelect?.(fileId);
  };

  const handleAddMetadata = () => {
    formik.setFieldValue('metadata', [...formik.values.metadata, { key: '', value: '' }]);
  };

  const handleRemoveMetadata = (index: number) => () => {
    formik.setFieldValue(
      'metadata',
      formik.values.metadata.filter((_, i) => i !== index)
    );
  };

  const { content } = formik.values;

  return (
    <>
      <div className={css.formSection}>
        <div className={css.sectionTitle}>Settings</div>

        <div className={css.settingsRow}>
          <FileSelector
            value={formik.values.iconId}
            label="Icon"
            required
            error={formik.errors.iconId}
            onSelect={handleIconSelect}
          />

          <Autocomplete
            freeSolo
            options={CATEGORY_SUB_TYPES}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
            value={
              CATEGORY_SUB_TYPES.find((t) => t.value === formik.values.categorySubType) ?? formik.values.categorySubType
            }
            onChange={(_, newValue) => {
              const value = typeof newValue === 'string' ? newValue : (newValue?.value ?? '');
              formik.setFieldValue('categorySubType', value);
            }}
            onInputChange={(_, inputValue, reason) => {
              if (reason === 'input') {
                formik.setFieldValue('categorySubType', inputValue);
              }
            }}
            sx={{ flex: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category Sub Type"
                size="small"
                required
                error={!!formik.errors.categorySubType}
                helperText={formik.errors.categorySubType}
              />
            )}
          />

          <Switch
            name="isPinned"
            label="Pin notification"
            checked={formik.values.isPinned}
            onChange={formik.handleChange}
          />
        </div>
      </div>

      <div className={css.formSection}>
        <div className={css.sectionTitle}>Content</div>

        <div className={css.localeFields}>
          <TextField
            name="content.title"
            label="Title"
            value={content.title}
            onChange={formik.handleChange}
            error={!!formik.errors.content?.title}
            helperText={formik.errors.content?.title}
            size="small"
            required
            fullWidth
          />

          <TextField
            name="content.description"
            label="Description"
            value={content.description}
            onChange={formik.handleChange}
            error={!!formik.errors.content?.description}
            helperText={formik.errors.content?.description}
            size="small"
            required
            fullWidth
            multiline
            rows={3}
          />

          <div className={css.twoColumns}>
            <TextField
              name="content.buttonText"
              label="Button Text"
              value={content.buttonText}
              onChange={formik.handleChange}
              error={!!formik.errors.content?.buttonText}
              helperText={formik.errors.content?.buttonText}
              size="small"
              required
              fullWidth
            />

            <TextField
              name="content.link"
              label="Link"
              value={content.link}
              onChange={formik.handleChange}
              error={!!formik.errors.content?.link}
              helperText={formik.errors.content?.link}
              size="small"
              required
              fullWidth
              placeholder="/path or https://..."
            />
          </div>
        </div>
      </div>

      <div className={css.formSection}>
        <div className={css.sectionTitle}>Metadata (optional)</div>

        <div className={css.metadataList}>
          {formik.values.metadata.map((entry, index) => (
            <div key={index} className={css.metadataItem}>
              <TextField
                name={`metadata.${index}.key`}
                label="Key"
                value={entry.key}
                onChange={formik.handleChange}
                size="small"
                sx={{ flex: 1 }}
              />

              <TextField
                name={`metadata.${index}.value`}
                label="Value"
                value={entry.value}
                onChange={formik.handleChange}
                size="small"
                sx={{ flex: 1 }}
              />

              <IconButton size="small" color="error" onClick={handleRemoveMetadata(index)}>
                <Trash2 size={16} />
              </IconButton>
            </div>
          ))}

          <Button size="small" variant="outlined" startIcon={<Plus size={14} />} onClick={handleAddMetadata}>
            Add Metadata
          </Button>
        </div>
      </div>
    </>
  );
};
